import React from "react";

import Typography from "@mui/material/Typography";

import ActionRequiredChip from "@app/analytics/components/ActionRequiredChip";
import ActiveChip from "@app/analytics/components/ActiveChip";
import IntegrationProviderIcon from "@app/analytics/components/IntegrationProviderIcon";
import ReauthIntegrationAlert from "@app/analytics/Integrations/components/ReauthIntegrationAlert";
import { getCategoryLabel } from "@app/analytics/Integrations/utils";
import useAuth from "@app/common/auth/useAuth";
import type { Integration } from "@app/common/types";
import * as PolicyUtils from "@app/common/utils/policyUtils";

import Link from "@rui/Link";
import { Column, Row } from "@rui/Stack";

import ConnectCta from "./ConnectCta";
import LearnMoreButton from "./LearnMoreButton";
import {
  CategoryLabel,
  Container,
  DisconnectButton,
  PremiumChip,
} from "./styles";

interface IntegrationDetailHeaderProps {
  /* Additional action that will show alongside the primary CTA. Will only show if the provider is connected */
  action?: React.ReactNode;
  children: React.ReactNode;
  cta?: {
    href?: string;
    isLoading?: boolean;
    label?: string;
    onClick?: () => void;
    disabled?: boolean;
    show?: boolean;
    tooltip?: string;
  };
  documentationUrl?: string;
  provider: Integration;
  isDisconnecting?: boolean;
  onClickDisconnect?: () => void;
  onReauth?: () => void;
}

const IntegrationDetailHeader = ({
  action = null,
  children,
  cta,
  documentationUrl,
  isDisconnecting,
  onClickDisconnect,
  onReauth,
  provider,
}: IntegrationDetailHeaderProps) => {
  const { user } = useAuth();
  const shouldShowCta = cta != null && (cta.show ?? !provider.isConnected);
  const showConnectDisclaimer = shouldShowCta && !provider.isDisabled;

  return (
    <Container>
      <Column spacing={2} flexGrow={1}>
        <Row spacing={1} variant="left">
          <IntegrationProviderIcon
            src={provider.icon}
            sx={{ padding: "10px" }}
          />
          <Column spacing={0.5}>
            <Row variant="left" spacing={1}>
              <Typography variant="bodyPrimaryMed">
                {provider.displayName}
              </Typography>
              {provider.limitedData ? (
                <ActionRequiredChip
                  label={
                    provider.reauthRequired ? "Action Needed" : "Limited Data"
                  }
                  severity="warning"
                />
              ) : provider.reauthRequired ? (
                <ActionRequiredChip severity="error" />
              ) : provider.isConnected ? (
                <ActiveChip />
              ) : null}
            </Row>
            <Row variant="left" spacing={0.5}>
              <CategoryLabel>
                {getCategoryLabel(provider.category)}
              </CategoryLabel>
              {provider.isPremium && <PremiumChip />}
            </Row>
          </Column>
        </Row>
        <Column>
          {children}
          {onReauth != null && provider.reauthRequired && (
            <ReauthIntegrationAlert onClickReauthorize={onReauth} />
          )}
        </Column>
        <Row variant="apart" spacing={1}>
          <Row variant="left" spacing={1} minWidth="fit-content">
            {shouldShowCta && <ConnectCta cta={cta} provider={provider} />}
            {provider.isConnected && action}
            {documentationUrl && <LearnMoreButton url={documentationUrl} />}
          </Row>
          <Row variant="right">
            {provider.isConnected && onClickDisconnect && (
              <DisconnectButton
                loading={isDisconnecting}
                onClick={onClickDisconnect}
              >
                Disconnect
              </DisconnectButton>
            )}
          </Row>
        </Row>
        {showConnectDisclaimer && (
          <Row flexShrink={1}>
            <Typography variant="labelReg" color="shades.neutral.500">
              By connecting this service, I agree to Read's{" "}
              <Link
                href={PolicyUtils.termsOfServiceForUser(user)}
                underline
                fontWeight={400}
              >
                Terms of Service
              </Link>{" "}
              and acknowledge I have read the{" "}
              <Link
                href={PolicyUtils.privacyPolicyForUser(user)}
                underline
                fontWeight={400}
              >
                Privacy Policy
              </Link>
              .
            </Typography>
          </Row>
        )}
      </Column>
    </Container>
  );
};

export default IntegrationDetailHeader;
